import React, { useEffect, useMemo, useState } from "react";
import Flex from "common/Boxes/Flex.tsx";
import TradingViewChart from "common/Charts/TradingViewChart.tsx";
import { TIME_RANGE_1_DAY } from "common/constants.ts";
import { useSymbolsMetadata } from "@shared/utils/hooks/misc.ts";
import CoinSelectionDropdown from "features/portfolio/Assets/CoinSelectionDropdown.tsx";
import ExchangeSelectionDropdown from "features/portfolio/Assets/ExchangeSelectionDropdown.tsx";
import { DEFAULT_PAIRS } from "features/portfolio/common/constants.ts";
import { mapValues } from "lodash";
import styled, { useTheme } from "styled-components";
import { LanguageNameSpaces } from "@shared/types";
import useTypedTranslation from "@shared/common/i18n/useTypedTranslation";
import { Panel } from "common/NewLook";

interface TradingViewPriceChartProps {
  coin: string;
  isSymbol?: boolean;
  defaultExchange?: string;
  isLoading?: boolean;
}

const TradingViewPriceChart = ({
  coin,
  isSymbol,
  defaultExchange,
  isLoading,
}: TradingViewPriceChartProps) => {
  const { exchangeToSymbols, getSymbolExchanges } = useSymbolsMetadata();
  const { t } = useTypedTranslation(LanguageNameSpaces.PORTFOLIO);
  const { isNewLook } = useTheme();

  const [symbol, setSymbol] = useState<string | undefined>(
    isSymbol ? coin : undefined
  );
  const [exchange, setExchange] = useState<string | undefined>(defaultExchange);

  const exchangeToSymbolsMapping = useMemo(() => {
    return mapValues(exchangeToSymbols, (symbols) => {
      return [...symbols].filter((symbol) => {
        const symbolArray = symbol.split("/");
        return (
          symbolArray[0] === coin ||
          (symbolArray[0] === "BTC" && symbolArray[1] === coin)
        );
      });
    });
  }, [exchangeToSymbols, coin]);

  const exchanges = useMemo(() => {
    return Object.keys(exchangeToSymbolsMapping).filter(
      (exchange) => exchangeToSymbolsMapping[exchange].length > 0
    );
  }, [exchangeToSymbolsMapping]);

  const symbols = useMemo(() => {
    if (exchange) {
      return exchangeToSymbolsMapping[exchange];
    }
  }, [exchange]);

  const symbolExchanges = useMemo(() => {
    return getSymbolExchanges(symbol);
  }, [symbol]);

  const exchangesToUse = isLoading
    ? undefined
    : isSymbol
    ? symbolExchanges
    : exchanges;

  useEffect(() => {
    if (!exchange && defaultExchange) {
      setExchange(defaultExchange);
    }
  }, [exchange, defaultExchange]);

  const getDefaultSymbol = () => {
    let symbol = symbols?.[0];
    for (let pair of DEFAULT_PAIRS) {
      const found = symbols?.find(
        (symbol: string) => symbol.split("/")[1] === pair
      );
      if (found) {
        symbol = found;
        break;
      }
    }
    return symbol;
  };

  useEffect(() => {
    if (!isSymbol && symbols && !symbols.includes(symbol || "")) {
      setSymbol(getDefaultSymbol());
    }
  }, [symbols, symbol, isSymbol]);

  if (isNewLook)
    return (
      <Panel
        title={t("charts.price_change")}
        flush={true}
        actions={
          <NewLookSelectors>
            {!isSymbol && (
              <CoinSelectionDropdown
                setSymbol={setSymbol}
                symbol={symbol || ""}
                symbols={symbols}
                checkBlacklist={false}
              />
            )}
            <ExchangeSelectionDropdown
              setExchange={setExchange}
              exchange={exchange}
              exchanges={exchangesToUse}
            />
          </NewLookSelectors>
        }
      >
        <NewLookChart>
          <TradingViewChart
            symbol={symbol}
            exchange={exchange}
            defaultTimeRange={TIME_RANGE_1_DAY}
            spinnerSize="default"
            minimize={true}
          />
        </NewLookChart>
      </Panel>
    );

  return (
    <>
      <Flex $justifyContent="flex-end" style={{ marginBottom: 16 }}>
        {!isSymbol && (
          <CoinSelectionDropdown
            setSymbol={setSymbol}
            symbol={symbol || ""}
            symbols={symbols}
            checkBlacklist={false}
          />
        )}
        <ExchangeSelectionDropdown
          setExchange={setExchange}
          exchange={exchange}
          exchanges={exchangesToUse}
        />
      </Flex>
      <div style={{ height: 400, marginBottom: 28 }}>
        <TradingViewChart
          symbol={symbol}
          exchange={exchange}
          defaultTimeRange={TIME_RANGE_1_DAY}
          spinnerSize="default"
          minimize={true}
        />
      </div>
    </>
  );
};

export default React.memo(TradingViewPriceChart);

const NewLookSelectors = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin-right: -8px;
`;
const NewLookChart = styled.div`
  height: 360px;
  /* Same inset as the panel's title, so the chart lines up with it. */
  padding: 8px 24px 16px;
`;
